import { useCallback } from "react";

import { TabPath } from "@/constants/Navigation";
import { ExploreTestID, TextTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import ExploreResourcesOverviewCard from "@/ui/components/explore/ExploreResourcesOverviewCard";
import ViewAllCard from "@/ui/composed/cards/ViewAllCard";
import { SecondarySectionHeader, SidewaysHighlightText } from "@/ui/core";
import { SupportedWidths } from "@/ui/core/SidewaysHighlightText";
import type { FlatListProps } from "@/ui/layout";
import { CarouselWrapper, FlatList, HStack } from "@/ui/layout";
import { toAbsoluteTabPath } from "@/utils/navigation.util";
import { joinTestIDs, toTestID } from "@/utils/string.util";

interface ExploreResourcesCarouselProps {
  data: Resource[];
  isMostPopularVideos?: boolean;
  title: string;
}

const ExploreResourcesCarousel = ({
  data,
  isMostPopularVideos = false,
  title,
}: ExploreResourcesCarouselProps) => {
  const titleTestID = toTestID(title);
  const renderItem = useCallback<
    NonNullable<FlatListProps<Resource>["renderItem"]>
  >(
    ({ index, item }) => {
      return (
        <HStack gap={18}>
          {isMostPopularVideos && index === 0 && (
            <SidewaysHighlightText
              text="Most popular"
              width={SupportedWidths.ExploreResourcesOverviewCard}
            />
          )}
          <ExploreResourcesOverviewCard
            carouselIndex={index}
            carouselTitle={title}
            imageUrl={
              typeof item.imageUrl === "string"
                ? { uri: item.imageUrl }
                : item.imageUrl
            }
            link={item.link}
            testID={joinTestIDs(
              ExploreTestID.ResourcesCarousel,
              titleTestID,
              index.toString(),
              TextTestID.Card,
            )}
            title={item.title}
            type={item.type}
          />
          {isMostPopularVideos && index === data.length - 1 && (
            <ViewAllCard
              onPress={() => {
                router.navigate(toAbsoluteTabPath(TabPath.Explore));
              }}
              testID={ExploreTestID.ResourcesCarousel}
            />
          )}
        </HStack>
      );
    },
    [data.length, isMostPopularVideos, title, titleTestID],
  );

  return (
    <CarouselWrapper
      hasPaddingTop={!isMostPopularVideos}
      header={
        <SecondarySectionHeader
          testID={joinTestIDs(
            ExploreTestID.ResourcesCarousel,
            titleTestID,
            TextTestID.Header,
          )}
          title={title}
        />
      }
    >
      <FlatList
        data={data}
        isHorizontal
        // eslint-disable-next-line max-params -- FlatList keyExtractor requires both item and index
        keyExtractor={(item, index) => `${item.link}-${index}`}
        renderItem={renderItem}
        shouldRemoveContentVerticalPadding
        testID={joinTestIDs(
          ExploreTestID.ResourcesCarousel,
          titleTestID,
          "scrollview",
        )}
      />
    </CarouselWrapper>
  );
};

export type { ExploreResourcesCarouselProps };
export default ExploreResourcesCarousel;
